/**
 * Booking service - CRUD operations, types, and re-exports.
 *
 * Structure:
 *   bookingService.ts            - Types, CRUD (create, get), data transform
 *   bookingActionsService.ts     - Cancel, modify, status, refund, complete
 *   bookingAvailabilityService.ts - Availability checks, slot availability
 */

import { apiFetch } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────

export interface BookingFilters {
  status?: string;
  paymentStatus?: string;
  boatId?: string;
  clientId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface BookingData {
  boatId: string;
  guestId: string;
  checkIn: string;
  checkOut: string;
  checkInTime?: string;
  checkOutTime?: string;
  guestsCount: number;
  nights: number;
  basePrice: number;
  cleaningFee?: number;
  extrasTotal?: number;
  shortStaySurchargeAmount?: number;
  shortStaySurchargePercent?: number;
  totalPrice: number;
  specialRequests?: string;
  selectedExtras?: { extraId: string; quantity: number; unitPrice: number; totalPrice: number }[];
  externalServiceIds?: string[];
  paymentMethod?: string | null;
  // Half-day series: mappa data 'YYYY-MM-DD' → segmento. Quando presente,
  // checkInTime/checkOutTime sono indicativi e il backend deve calcolare il
  // totale per data dalla mappa.
  dayParts?: Record<string, 'full' | 'morning' | 'afternoon'>;
  // Hourly rental: durata in ore + snapshot rental_mode applicato. Usato solo
  // se boat.rental_mode='hourly'. checkIn=checkOut=stessa data.
  durationHours?: number;
  rentalModeUsed?: 'daily' | 'weekly' | 'monthly' | 'hourly';
  // Punto di partenza per-azienda scelto dal cliente (vuoto = fallback porto barca).
  departurePointId?: string;
}

export interface CancellationPreview {
  bookingId: string;
  totalPrice: number;
  refundPercent: number;
  refundAmount: number;
  daysBeforeCheckIn: number;
  policyLabel: string;
  message: string;
}

export interface BookingModifyData {
  checkIn?: string;
  checkOut?: string;
  checkInTime?: string;
  checkOutTime?: string;
  guestsCount?: number;
  specialRequests?: string;
  selectedExtras?: { extraId: string; quantity: number; unitPrice: number; totalPrice: number }[];
}

export interface Booking {
  id: string;
  boatId: string;
  clientId: string;
  pickupDate: string;
  returnDate: string;
  pickupTime?: string;
  returnTime?: string;
  passengersCount: number;
  days: number;
  basePrice: number;
  insuranceFee: number;
  extrasTotal: number;
  totalPrice: number;
  listPrice?: number;         // prezzo listino (stagioni/fascia) al momento della prenotazione manuale
  discountPercent?: number;   // sconto % applicato dal partner sul listino
  vendorPayout?: number;
  platformFee?: number;
  commissionRate?: number;
  depositAmount?: number;
  depositRefunded?: boolean;
  depositRefundedAt?: string;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  paymentStatus: 'PENDING' | 'PAID' | 'PARTIAL' | 'REFUNDED' | 'DEPOSIT_REFUNDED' | 'REFUND_PENDING' | 'REFUND_PROCESSING' | 'REFUND_FAILED' | 'REFUND_WAIVED';
  paymentMethod?: string;
  paymentGateway?: 'MOLLIE' | 'MANUAL' | 'BANK_TRANSFER' | 'CASH' | string;
  molliePaymentId?: string;
  mollieRefundId?: string;
  specialRequests?: string;
  departurePointName?: string;   // F1: nome del punto di partenza scelto
  cancellationReason?: string;
  cancelledAt?: string;
  refundAmount?: number;
  refundPercent?: number;
  cancellationPolicyApplied?: string;
  // Cancellation request flow (cliente → vendor approva/rifiuta)
  cancellationRequestStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  cancellationRequestReason?: 'BAD_WEATHER' | 'HEALTH' | 'EMERGENCY' | 'FORCE_MAJEURE' | 'OTHER' | null;
  cancellationRequestNote?: string | null;
  cancellationRequestedAt?: string | null;
  cancellationDecidedAt?: string | null;
  cancellationResponseNote?: string | null;
  // Cauzione (transazione separata da total_price)
  securityDeposit?: {
    amount: number;
    method: 'MANUAL' | 'BANK_TRANSFER' | 'CARD_CHARGE' | 'CARD_HOLD' | null;
    status: 'NOT_REQUIRED' | 'PENDING' | 'HELD' | 'CHARGED' | 'REFUNDED' | 'RELEASED' | 'FAILED';
    paymentId?: string | null;
    refundId?: string | null;
    chargedAt?: string | null;
    releasedAt?: string | null;
  };
  boat?: {
    id: string;
    name: string;
    image_urls: string[];
    port: string;
    cancellation_policy?: string;
    bookings_suspended?: boolean;
    owner?: {
      id: string;
      name: string;
      email: string;
      role: 'VENDOR' | 'COMPANY' | 'ADMIN';
      company_name?: string;
      is_suspended?: boolean;
      bookings_suspended?: boolean;
    };
  };
  client?: {
    id: string;
    name: string;
    email: string;
  };
  extras?: {
    id: string;
    name: string;
    price: number;
  }[];
  createdAt: string;
  updatedAt: string;
}

// ─── Data Transform ─────────────────────────────────────────────────────

const transformBookingBoat = (raw: unknown): Booking['boat'] => {
  if (!raw || typeof raw !== 'object') return undefined;
  const b = raw as Record<string, unknown>;
  const users = b.users as Record<string, unknown> | undefined;
  const ownerFromUsers = users
    ? {
        id: users.id as string,
        name: users.name as string,
        email: users.email as string,
        role: users.role as 'VENDOR' | 'COMPANY' | 'ADMIN',
        company_name: users.company_name as string | undefined,
        is_suspended: users.is_suspended as boolean | undefined,
        bookings_suspended: users.bookings_suspended as boolean | undefined,
      }
    : (b.owner as NonNullable<Booking['boat']>['owner'] | undefined);
  return {
    id: b.id as string,
    name: b.name as string,
    image_urls: (b.image_urls as string[]) ?? [],
    port: ((b.port ?? b.location) as string) ?? '',
    cancellation_policy: b.cancellation_policy as string | undefined,
    bookings_suspended: b.bookings_suspended as boolean | undefined,
    owner: ownerFromUsers,
  };
};

export const transformBookingData = (apiBooking: Record<string, unknown>): Booking => {
  const safeParseFloat = (val: unknown): number => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val) || 0;
    return 0;
  };

  return {
    id: apiBooking.id as string,
    boatId: apiBooking.boat_id as string,
    clientId: (apiBooking.guest_id || apiBooking.client_id) as string,
    pickupDate: apiBooking.check_in as string,
    returnDate: apiBooking.check_out as string,
    pickupTime: apiBooking.check_in_time as string | undefined,
    returnTime: apiBooking.check_out_time as string | undefined,
    passengersCount: (apiBooking.guests_count || apiBooking.passengers_count) as number,
    days: apiBooking.nights as number,
    basePrice: safeParseFloat(apiBooking.base_price),
    insuranceFee: safeParseFloat(apiBooking.cleaning_fee || apiBooking.insurance_fee),
    extrasTotal: safeParseFloat(apiBooking.extras_total),
    totalPrice: safeParseFloat(apiBooking.total_price),
    listPrice: apiBooking.list_price != null ? safeParseFloat(apiBooking.list_price) : undefined,
    discountPercent: apiBooking.discount_percent != null ? safeParseFloat(apiBooking.discount_percent) : undefined,
    vendorPayout: apiBooking.vendor_payout != null ? safeParseFloat(apiBooking.vendor_payout) : undefined,
    platformFee: apiBooking.platform_fee != null ? safeParseFloat(apiBooking.platform_fee) : undefined,
    commissionRate: apiBooking.commission_rate != null ? safeParseFloat(apiBooking.commission_rate) : undefined,
    depositAmount: apiBooking.deposit_amount ? safeParseFloat(apiBooking.deposit_amount) : undefined,
    depositRefunded: apiBooking.deposit_refunded as boolean | undefined,
    depositRefundedAt: apiBooking.deposit_refunded_at as string | undefined,
    status: apiBooking.status as Booking['status'],
    paymentStatus: apiBooking.payment_status as Booking['paymentStatus'],
    paymentMethod: apiBooking.payment_method as string | undefined,
    paymentGateway: apiBooking.payment_gateway as Booking['paymentGateway'],
    molliePaymentId: apiBooking.mollie_payment_id as string | undefined,
    mollieRefundId: apiBooking.mollie_refund_id as string | undefined,
    specialRequests: apiBooking.special_requests as string | undefined,
    departurePointName: (apiBooking.departure_point as { name?: string } | null | undefined)?.name ?? undefined,
    cancellationReason: apiBooking.cancellation_reason as string | undefined,
    cancelledAt: apiBooking.cancelled_at as string | undefined,
    refundAmount: apiBooking.refund_amount ? safeParseFloat(apiBooking.refund_amount) : undefined,
    refundPercent: apiBooking.refund_percent as number | undefined,
    cancellationPolicyApplied: apiBooking.cancellation_policy_applied as string | undefined,
    cancellationRequestStatus: apiBooking.cancellation_request_status as Booking['cancellationRequestStatus'],
    cancellationRequestReason: apiBooking.cancellation_request_reason as Booking['cancellationRequestReason'],
    cancellationRequestNote: apiBooking.cancellation_request_note as string | null | undefined,
    cancellationRequestedAt: apiBooking.cancellation_requested_at as string | null | undefined,
    cancellationDecidedAt: apiBooking.cancellation_decided_at as string | null | undefined,
    cancellationResponseNote: apiBooking.cancellation_response_note as string | null | undefined,
    // Cauzione (transazione separata): snapshot al booking + stato live aggiornato dal webhook deposit-only
    securityDeposit: apiBooking.security_deposit_status
      ? {
          amount: safeParseFloat(apiBooking.security_deposit_amount),
          method: (apiBooking.security_deposit_method as NonNullable<Booking['securityDeposit']>['method']) ?? null,
          status: apiBooking.security_deposit_status as NonNullable<Booking['securityDeposit']>['status'],
          paymentId: apiBooking.security_deposit_payment_id as string | null | undefined,
          refundId: apiBooking.security_deposit_refund_id as string | null | undefined,
          chargedAt: apiBooking.security_deposit_charged_at as string | null | undefined,
          releasedAt: apiBooking.security_deposit_released_at as string | null | undefined,
        }
      : undefined,
    boat: transformBookingBoat(apiBooking.boats ?? apiBooking.boat),
    // client: prova in ordine guest → client → users (Prisma relation)
    ...(() => {
      const raw = (apiBooking.guest ?? apiBooking.client ?? apiBooking.users) as
        | Record<string, unknown>
        | undefined;
      if (!raw) return {};
      const composed = [raw.first_name, raw.last_name].filter(Boolean).join(' ').trim();
      const name = (raw.name as string | undefined) ?? (composed || undefined);
      return {
        client: {
          id: raw.id as string,
          name: name || (raw.email as string) || '',
          email: (raw.email as string) || '',
        },
      };
    })(),
    extras: apiBooking.extras as Booking['extras'],
    createdAt: apiBooking.created_at as string,
    updatedAt: apiBooking.updated_at as string
  };
};

// ─── CRUD Operations ────────────────────────────────────────────────────

export const getAllBookings = async (filters: BookingFilters = {}): Promise<{ success: boolean; data: Booking[] }> => {
  const queryParams = new URLSearchParams();
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);
  if (filters.boatId) queryParams.append('boatId', filters.boatId);
  if (filters.clientId) queryParams.append('clientId', filters.clientId);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  if (filters.limit) queryParams.append('limit', filters.limit.toString());
  if (filters.offset) queryParams.append('offset', filters.offset.toString());

  const qs = queryParams.toString();
  const data = await apiFetch<{ success: boolean; data: Record<string, unknown>[] }>(`/bookings${qs ? '?' + qs : ''}`);
  return { ...data, data: (data.data || []).map((b) => transformBookingData(b)) };
};

export const getBookingById = async (id: string): Promise<{ success: boolean; data: Booking }> => {
  return apiFetch<{ success: boolean; data: Booking }>(`/bookings/${id}`);
};

export const getBookingsByClientId = async (clientId: string): Promise<{ success: boolean; data: Booking[] }> => {
  const data = await apiFetch<{ success: boolean; data: Record<string, unknown>[] }>(`/bookings/client/${clientId}`);
  return { ...data, data: (data.data || []).map((b) => transformBookingData(b)) };
};

export const getBookingsByBoatId = async (boatId: string): Promise<{ success: boolean; data: Booking[] }> => {
  const data = await apiFetch<{ success: boolean; data: Record<string, unknown>[] }>(`/bookings/boat/${boatId}`);
  return { ...data, data: (data.data || []).map((b) => transformBookingData(b)) };
};

export const createBooking = async (bookingData: BookingData): Promise<{ success: boolean; data: Booking }> => {
  return apiFetch<{ success: boolean; data: Booking }>('/bookings', {
    method: 'POST',
    body: JSON.stringify(bookingData),
  });
};

// ─── Re-exports from split services ─────────────────────────────────────

export { cancelBooking, getCancellationPreview, modifyBooking, updateBookingStatus, refundDeposit, markAsCompleted } from './bookingActionsService';
export { checkAvailability, getSlotAvailability } from './bookingAvailabilityService';
export type { SlotAvailability, SlotAvailabilityResponse } from './bookingAvailabilityService';

// ─── Default export ─────────────────────────────────────────────────────

export default {
  getAllBookings,
  getBookingById,
  getBookingsByClientId,
  getBookingsByBoatId,
  createBooking,
  transformBookingData
};
